import { animate, style, transition, trigger } from '@angular/animations';
import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { routePath } from '@brightside-web/desktop/data-access/core-services';
import { ServiceInventoryService, ServiceState } from '@brightside-web/desktop/data-access/service-inventory';
import { CreditSummary } from '@brightside-web/desktop/data-access/shared';
import { BrightsideUiModule, HeaderLabelCellConfig } from '@brightside/brightside-ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CreditService } from 'libs/desktop/data-access/credit/src/lib/credit.service';
import { MicroUiMoleculeModule } from 'libs/micro-ui/molecule/src/lib/micro-ui-molecule.module';
import { SharedDesktopModule } from 'libs/shared/desktop/src/lib/shared-desktop.module';
import { Subject, catchError, filter, map, of, takeUntil, tap } from 'rxjs';

@Component({
    selector: 'brightside-web-credit-complete-card',
    templateUrl: './credit-complete-card.component.html',
    styleUrls: ['./credit-complete-card.component.scss'],
    imports: [BrightsideUiModule, TranslateModule, SharedDesktopModule],
    providers: [DatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('fadeIn', [transition(':enter', [style({ opacity: 0 }), animate('500ms ease-out', style({ opacity: 1 }))])]),
    ]
})
export class CreditCompleteCardComponent implements OnInit {
  creditCompleteCellConfig: HeaderLabelCellConfig;
  showRetryAccessory: boolean = false;
  isApiError: boolean = false;
  vantageVisible: boolean = false;

  private _router: Router = inject(Router);
  private _translateService: TranslateService = inject(TranslateService);
  private _datePipe: DatePipe = inject(DatePipe);
  private _creditService: CreditService = inject(CreditService);
  private _cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private _ubsub$: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.setCreditCompleteCardLoading();
    this.getCreditSummary();
  }

  getCreditSummary(): void {
    this._creditService
      .getCreditSummary()
      .pipe(
        map((res) => ({
          creditScore: res?.creditScore?.score,
          lastUpdated: res?.creditScore?.reportDate,
        })),
        tap((res) => {
          this.handleSuccessResponse(res);
        }),
        catchError(() => of(this.handleErrorResponse()))
      )
      .subscribe();
  }

  navigateToCreditDashboard(): void {
    this._router.navigate([routePath('credit_dashboard')]);
  }

  creditCardContentAction(): void {
    if (this.isApiError) {
      this.showRetryAccessory = false;
      this.setCreditCompleteCardLoading();
      setTimeout(() => {
        this.getCreditSummary();
      }, 3000) 
    } else {
      this.vantageVisible = true;
    }    
  }

  ngOnDestroy(): void {
    this._ubsub$.next();
  }

  private handleSuccessResponse(creditInfo: { creditScore: number; lastUpdated: number }) {
    this.creditCompleteCellConfig = {
      leftTitle: this._translateService.instant('CREDIT_SCORE'),
      leftTitleSemibold: true,
      rightIconName: 'chevron_right',
      rightIconColor: 'var(--blue-50)',
      rightSubtitle: this._translateService.instant('UPDATED_DATE', {
        date: this._datePipe.transform(creditInfo.lastUpdated, 'shortDate'),
      }),
      rightTitle: `${creditInfo?.creditScore}`,
      rightTitleSemibold: true,
      rightSubtitleNoWrap: true,
      bgColor: 'var(--white)',
    };
    this.showRetryAccessory = true;
    this._cdr.detectChanges();
  }

  private setCreditCompleteCardLoading(): void {
    this.creditCompleteCellConfig = {
      leftTitle: this._translateService.instant('CREDIT_SCORE'),
      leftTitleSemibold: true,
      bgColor: 'var(--white)',
      lottieLoading: true,
      minHeight: '2.2rem',
    };
    this.showRetryAccessory = false;
    this._cdr.detectChanges();
  }

  private showRetryAccessoryCard(): void {
    this.creditCompleteCellConfig = {
      leftTitle: this._translateService.instant('CREDIT_SCORE'),
      leftTitleSemibold: true,
      bgColor: 'var(--white)',
      minHeight: '2.2rem',
    };
    this.showRetryAccessory = true;    
    this._cdr.detectChanges();
  }

  private handleErrorResponse() {
    this.isApiError = true;
    this.showRetryAccessoryCard();
  }
}
